import { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { answerWithMcp } from "../../services/mcp/mcpAssistant";
import ModuleDashboard from "./ModuleDashboard";
import "./AiAssistant.css";

const SEARCH_HISTORY_KEY = "ai-assistant-search-history";
const SEARCH_HISTORY_LIMIT = 30;
const TEXTAREA_MAX_HEIGHT = 140;

const SUGGESTIONS = [
  { icon: "bi-grid-1x2", title: "Open dashboard", prompt: "dashboard" },
  { icon: "bi-receipt", title: "Pending purchase orders", prompt: "Show pending purchase orders" },
  { icon: "bi-box-seam", title: "Recent GRN status", prompt: "What is the status of recent GRNs?" },
  { icon: "bi-tag", title: "SKU pricing", prompt: "Show SKU prices" },
];

function loadSearchHistory() {
  try {
    const raw = localStorage.getItem(SEARCH_HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
}

function persistSearchHistory(entries) {
  try {
    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(entries.slice(0, SEARCH_HISTORY_LIMIT)));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

function formatHistoryTime(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Render inline markdown: **bold**, *italic*, `code`. */
function renderInline(text) {
  const tokens = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);

  return tokens.map((token, i) => {
    if (!token) return null;
    if (token.startsWith("**") && token.endsWith("**")) {
      return <strong key={i}>{token.slice(2, -2)}</strong>;
    }
    if (token.startsWith("*") && token.endsWith("*") && token.length > 2) {
      return <em key={i}>{token.slice(1, -1)}</em>;
    }
    if (token.startsWith("`") && token.endsWith("`")) {
      return <code key={i} className="ai-assistant__inline-code">{token.slice(1, -1)}</code>;
    }
    return <span key={i}>{token}</span>;
  });
}

/** Render one markdown line as a heading, bullet, or paragraph. */
function MarkdownLine({ line }) {
  const trimmed = line.trim();

  if (!trimmed) return <div className="ai-assistant__md-spacer" />;

  const headingMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
  if (headingMatch) {
    const level = headingMatch[1].length;
    return (
      <div className={`ai-assistant__md-heading ai-assistant__md-heading--h${level}`}>
        {renderInline(headingMatch[2])}
      </div>
    );
  }

  const bulletMatch = trimmed.match(/^[-*•]\s+(.*)$/);
  if (bulletMatch) {
    return (
      <div className="ai-assistant__md-bullet">
        <span className="ai-assistant__md-bullet-dot" aria-hidden="true">•</span>
        <span className="ai-assistant__md-bullet-text">{renderInline(bulletMatch[1])}</span>
      </div>
    );
  }

  const numberedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
  if (numberedMatch) {
    return (
      <div className="ai-assistant__md-bullet">
        <span className="ai-assistant__md-bullet-num" aria-hidden="true">{numberedMatch[1]}.</span>
        <span className="ai-assistant__md-bullet-text">{renderInline(numberedMatch[2])}</span>
      </div>
    );
  }

  return <div className="ai-assistant__md-paragraph">{renderInline(trimmed)}</div>;
}

/** Render streamed markdown text; shows a blinking cursor while still streaming. */
function StreamingMarkdown({ text = "", streaming = false }) {
  const lines = String(text).split("\n");

  return (
    <div className="ai-assistant__typewriter">
      {lines.map((line, index) => (
        <div key={index} className="ai-assistant__typewriter-line">
          <MarkdownLine line={line} />
        </div>
      ))}
      {streaming && <span className="ai-assistant__cursor" aria-hidden="true" />}
    </div>
  );
}

/** Waiting indicator shown while the model is thinking. */
function Thinking() {
  return (
    <div className="ai-assistant__thinking">
      <span className="ai-assistant__thinking-text">Thinking</span>
      <span className="ai-assistant__thinking-dots" aria-hidden="true">
        <span className="ai-assistant__thinking-dot" />
        <span className="ai-assistant__thinking-dot" />
        <span className="ai-assistant__thinking-dot" />
      </span>
    </div>
  );
}

function AiAssistant({ onClose }) {
  const [query, setQuery] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [searchHistory, setSearchHistory] = useState(() => loadSearchHistory());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [listening, setListening] = useState(false);

  const scrollRef = useRef(null);
  const autoScrollRef = useRef(true);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const baseQueryRef = useRef("");
  const abortControllerRef = useRef(null);
  const messageIdRef = useRef(0);
  const canvasRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const rafRef = useRef(null);
  const historyWrapRef = useRef(null);

  const speechSupported =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const nextMessageId = useCallback(() => {
    messageIdRef.current += 1;
    return messageIdRef.current;
  }, []);

  /** Scroll to the newest message, but only while the user is pinned to the bottom. */
  const scrollToBottom = useCallback(() => {
    if (!autoScrollRef.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  /** Re-evaluate whether the user is at the bottom whenever they scroll. */
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    autoScrollRef.current = distanceFromBottom <= 60;
  }, []);

  const teardownAudio = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    mediaStreamRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
      audioCtxRef.current.close();
    }
    audioCtxRef.current = null;
    analyserRef.current = null;
  }, []);

  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    if (canvas.width !== width * dpr || canvas.height !== height * dpr) {
      canvas.width = width * dpr;
      canvas.height = height * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const bufferLength = analyser.frequencyBinCount;
    const data = new Uint8Array(bufferLength);

    const render = () => {
      analyser.getByteTimeDomainData(data);
      ctx.clearRect(0, 0, width, height);

      const barCount = Math.floor(width / 4);
      const step = Math.floor(bufferLength / barCount) || 1;
      const barWidth = 2;
      const gap = 2;

      ctx.fillStyle = "#2b3991";
      for (let i = 0; i < barCount; i += 1) {
        const v = (data[i * step] - 128) / 128;
        const amp = Math.max(0.04, Math.abs(v));
        const barHeight = amp * height * 0.9;
        const x = i * (barWidth + gap);
        const y = (height - barHeight) / 2;
        ctx.fillRect(x, y, barWidth, barHeight);
      }

      rafRef.current = requestAnimationFrame(render);
    };

    render();
  }, []);

  const startAudioVisualizer = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioCtx();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;
      requestAnimationFrame(drawWaveform);
    } catch (err) {
      console.warn("Microphone visualizer unavailable:", err);
    }
  }, [drawWaveform]);

  const startListening = useCallback(() => {
    if (!speechSupported) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = true;
    recognition.interimResults = true;

    baseQueryRef.current = query ? `${query.trim()} ` : "";

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      setQuery(`${baseQueryRef.current}${transcript}`);
    };

    recognition.onerror = () => {
      teardownAudio();
      setListening(false);
    };
    recognition.onend = () => {
      teardownAudio();
      setListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
    startAudioVisualizer();
  }, [query, speechSupported, startAudioVisualizer, teardownAudio]);

  /** Confirm: keep the transcribed text and focus the input. */
  const confirmListening = useCallback(() => {
    recognitionRef.current?.stop();
    teardownAudio();
    setListening(false);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, [teardownAudio]);

  /** Cancel: discard whatever was transcribed in this session. */
  const cancelListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (recognition) {
      recognition.onresult = null;
      recognition.stop();
    }
    teardownAudio();
    setQuery(baseQueryRef.current.trimEnd());
    setListening(false);
  }, [teardownAudio]);

  useEffect(
    () => () => {
      recognitionRef.current?.stop();
      teardownAudio();
    },
    [teardownAudio]
  );

  useEffect(() => {
    if (!historyOpen) return undefined;
    const onPointerDown = (e) => {
      if (historyWrapRef.current && !historyWrapRef.current.contains(e.target)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [historyOpen]);

  const pushSearchHistory = useCallback((text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed) return;

    setSearchHistory((prev) => {
      const next = [
        { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, text: trimmed, at: Date.now() },
        ...prev.filter((item) => item.text.toLowerCase() !== trimmed.toLowerCase()),
      ].slice(0, SEARCH_HISTORY_LIMIT);
      persistSearchHistory(next);
      return next;
    });
  }, []);

  const clearSearchHistory = useCallback(() => {
    setSearchHistory([]);
    persistSearchHistory([]);
  }, []);

  const removeSearchHistoryItem = useCallback((id) => {
    setSearchHistory((prev) => {
      const next = prev.filter((item) => item.id !== id);
      persistSearchHistory(next);
      return next;
    });
  }, []);

  const handleCopy = useCallback(async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      const temp = document.createElement("textarea");
      temp.value = text;
      document.body.appendChild(temp);
      temp.select();
      document.execCommand("copy");
      document.body.removeChild(temp);
    }
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex((current) => (current === index ? null : current)), 1500);
  }, []);

  const handleEdit = useCallback((text) => {
    setQuery(text);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }, []);

  useEffect(() => {
    requestAnimationFrame(scrollToBottom);
  }, [chatHistory, loading, scrollToBottom]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, TEXTAREA_MAX_HEIGHT)}px`;
  }, [query, listening]);

  const setMessage = (id, patch) => {
    setChatHistory((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...patch } : m))
    );
  };

  /**
   * Query the MCP server and reveal the answer incrementally in the chat UI.
   * When `existingId` is provided, that message is re-streamed in place
   * (used by "Try again"); otherwise a new AI message is appended.
   */
  const streamAnswer = async (userText, existingId = null) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const aiId = existingId ?? nextMessageId();
    setLoading(true);

    if (existingId != null) {
      setMessage(aiId, { text: "", streaming: true, dashboard: null });
    } else {
      setChatHistory((prev) => [
        ...prev,
        { id: aiId, sender: "ai", text: "", streaming: true, dashboard: null },
      ]);
    }

    const finalize = (patch) => setMessage(aiId, { ...patch, streaming: false });

    let acc = "";
    let dashboard = null;

    const flushText = () => {
      flushSync(() => {
        setMessage(aiId, { text: acc, dashboard });
      });
    };

    try {
      const result = await answerWithMcp(userText, controller.signal, (update) => {
        if (update?.text != null) acc = update.text;
        if (update?.dashboard !== undefined) dashboard = update.dashboard;
        flushText();
      });
      if (result?.dashboard) dashboard = result.dashboard;
      if (result?.text) acc = result.text;
      finalize({ text: acc || "No response received.", dashboard });
    } catch (error) {
      if (error.name === "AbortError") {
        finalize({ text: acc, dashboard });
        return;
      }
      console.error(error);
      finalize({
        text: acc || error.message || "Sorry, I encountered an error connecting to the MCP server.",
        dashboard,
      });
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const stopAnswer = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setLoading(false);
    setChatHistory((prev) =>
      prev.map((m) => (m.streaming ? { ...m, streaming: false } : m))
    );
  }, []);

  const runUserQuery = async (userText) => {
    const text = String(userText || "").trim();
    if (!text || loading) return;

    setHistoryOpen(false);
    autoScrollRef.current = true;
    pushSearchHistory(text);
    setChatHistory((prev) => [
      ...prev,
      { id: nextMessageId(), sender: "user", text },
    ]);
    setQuery("");
    await streamAnswer(text);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (listening) confirmListening();
    if (!query.trim() || loading) return;
    await runUserQuery(query);
  };

  const handleHistorySelect = async (text) => {
    if (loading) return;
    await runUserQuery(text);
  };

  const handleRegenerate = async (aiIndex) => {
    if (loading) return;

    let userText = "";
    for (let i = aiIndex - 1; i >= 0; i -= 1) {
      if (chatHistory[i]?.sender === "user") {
        userText = chatHistory[i].text;
        break;
      }
    }
    if (!userText) return;

    const targetId = chatHistory[aiIndex]?.id ?? null;
    await streamAnswer(userText, targetId);
  };

  const isStreaming = chatHistory.some((m) => m.streaming);
  const isAnswering = loading || isStreaming;

  return (
    <div className="ai-assistant">
      <header className="ai-assistant__header">
        <div className="ai-assistant__header-content">
          <div className="ai-assistant__logo-area">
            <span className="ai-assistant__logo-icon" aria-hidden="true">
              <i className="bi bi-stars" />
            </span>
            <div className="ai-assistant__logo-text-wrap">
              <span className="ai-assistant__logo-text">AI Assistant</span>
              <span className="ai-assistant__online">
                <span className="ai-assistant__online-dot" aria-hidden="true" />
                Online
              </span>
            </div>
          </div>

          <div className="ai-assistant__header-actions">
            <div className="ai-assistant__history-wrap" ref={historyWrapRef}>
              <button
                type="button"
                className={`ai-assistant__history-btn${historyOpen ? " ai-assistant__history-btn--active" : ""}`}
                onClick={() => setHistoryOpen((open) => !open)}
                title="Search history"
                aria-label="Search history"
                aria-expanded={historyOpen}
              >
                <i className="bi bi-clock-history" aria-hidden="true" />
                {searchHistory.length > 0 && (
                  <span className="ai-assistant__history-badge">
                    {searchHistory.length > 99 ? "99+" : searchHistory.length}
                  </span>
                )}
              </button>

              {historyOpen && (
                <div className="ai-assistant__history-panel" role="dialog" aria-label="Search history">
                  <div className="ai-assistant__history-panel-head">
                    <span>Search History</span>
                    {searchHistory.length > 0 && (
                      <button
                        type="button"
                        className="ai-assistant__history-clear"
                        onClick={clearSearchHistory}
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {searchHistory.length === 0 ? (
                    <div className="ai-assistant__history-empty">
                      No previous searches yet.
                      <br />
                      Your questions will appear here.
                    </div>
                  ) : (
                    <ul className="ai-assistant__history-list">
                      {searchHistory.map((item) => (
                        <li key={item.id} className="ai-assistant__history-item">
                          <button
                            type="button"
                            className="ai-assistant__history-item-main"
                            onClick={() => handleHistorySelect(item.text)}
                            disabled={isAnswering}
                            title="Run this search again"
                          >
                            <i className="bi bi-search" aria-hidden="true" />
                            <span className="ai-assistant__history-item-body">
                              <span className="ai-assistant__history-item-text">{item.text}</span>
                              <span className="ai-assistant__history-item-time">
                                {formatHistoryTime(item.at)}
                              </span>
                            </span>
                          </button>
                          <button
                            type="button"
                            className="ai-assistant__history-item-remove"
                            onClick={() => removeSearchHistoryItem(item.id)}
                            title="Remove"
                            aria-label={`Remove search: ${item.text}`}
                          >
                            <i className="bi bi-x" aria-hidden="true" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {onClose && (
              <button
                type="button"
                className="ai-assistant__close-btn"
                onClick={onClose}
                aria-label="Close AI Assistant"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="ai-assistant__main" ref={scrollRef} onScroll={handleScroll}>
        <div className="ai-assistant__chat-wrapper">
          {chatHistory.length === 0 && !loading && (
            <div className="ai-assistant__welcome">
              <div className="ai-assistant__welcome-icon" aria-hidden="true">
                <i className="bi bi-stars" />
              </div>
              <h2 className="ai-assistant__welcome-title">How can I help you today?</h2>
              <p className="ai-assistant__welcome-subtitle">
                Ask specific questions (price, status, SKUs), or say{" "}
                <strong>dashboard</strong> for a visual PO / ASN / GRN / Sales / Shipment panel.
              </p>
              <div className="ai-assistant__suggestions">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s.title}
                    type="button"
                    className="ai-assistant__suggestion"
                    onClick={() => runUserQuery(s.prompt)}
                  >
                    <span className="ai-assistant__suggestion-icon" aria-hidden="true">
                      <i className={`bi ${s.icon}`} />
                    </span>
                    <span className="ai-assistant__suggestion-text">{s.title}</span>
                    <i className="bi bi-arrow-up-right ai-assistant__suggestion-arrow" aria-hidden="true" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {chatHistory.map((msg, index) => (
            <div
              key={msg.id ?? `${msg.sender}-${index}`}
              className={`ai-assistant__message-row ${
                msg.sender === "user"
                  ? "ai-assistant__message-row--user"
                  : "ai-assistant__message-row--ai"
              }`}
            >
              {msg.sender === "ai" && (
                <div className="ai-assistant__avatar" aria-hidden="true">
                  <i className="bi bi-stars" />
                </div>
              )}
              <div
                className={`ai-assistant__message-content ${
                  msg.sender === "user" ? "ai-assistant__message-content--user" : ""
                }`}
              >
                <span
                  className={`ai-assistant__label ${
                    msg.sender === "user" ? "ai-assistant__label--user" : "ai-assistant__label--ai"
                  }`}
                >
                  {msg.sender === "user" ? "You" : "Assistant"}
                </span>
                <div
                  className={`ai-assistant__bubble ${
                    msg.sender === "user"
                      ? "ai-assistant__bubble--user"
                      : "ai-assistant__bubble--ai"
                  }`}
                >
                  {msg.sender === "ai" ? (
                    msg.streaming && !msg.text && !msg.dashboard ? (
                      <Thinking />
                    ) : (
                      <>
                        {msg.text ? (
                          <StreamingMarkdown text={msg.text} streaming={msg.streaming && !msg.dashboard} />
                        ) : null}
                        {msg.dashboard ? <ModuleDashboard data={msg.dashboard} /> : null}
                      </>
                    )
                  ) : (
                    msg.text
                  )}
                </div>

                {msg.sender === "user" && (
                  <div className="ai-assistant__msg-actions">
                    <button
                      type="button"
                      className="ai-assistant__action-btn"
                      onClick={() => handleCopy(msg.text, index)}
                      title="Copy"
                      aria-label="Copy message"
                    >
                      <i
                        className={copiedIndex === index ? "bi bi-check2" : "bi bi-copy"}
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      type="button"
                      className="ai-assistant__action-btn"
                      onClick={() => handleEdit(msg.text)}
                      title="Edit"
                      aria-label="Edit message"
                    >
                      <i className="bi bi-pencil" aria-hidden="true" />
                    </button>
                  </div>
                )}

                {msg.sender === "ai" && !msg.streaming && msg.text && (
                  <div className="ai-assistant__msg-actions ai-assistant__msg-actions--ai">
                    <button
                      type="button"
                      className="ai-assistant__action-btn"
                      onClick={() => handleCopy(msg.text, index)}
                      title="Copy"
                      aria-label="Copy response"
                    >
                      <i
                        className={copiedIndex === index ? "bi bi-check2" : "bi bi-copy"}
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      type="button"
                      className="ai-assistant__action-btn"
                      onClick={() => handleRegenerate(index)}
                      title="Try again"
                      aria-label="Regenerate response"
                      disabled={isAnswering}
                    >
                      <i className="bi bi-arrow-repeat" aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      <footer className="ai-assistant__footer">
        <form onSubmit={handleSubmit}>
          {listening ? (
            <div className="ai-assistant__record-wrapper">
              <button
                type="button"
                className="ai-assistant__record-btn ai-assistant__record-btn--cancel"
                onClick={cancelListening}
                title="Cancel"
                aria-label="Cancel voice input"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
              <canvas ref={canvasRef} className="ai-assistant__waveform" />
              <button
                type="button"
                className="ai-assistant__record-btn ai-assistant__record-btn--confirm"
                onClick={confirmListening}
                title="Done"
                aria-label="Confirm voice input"
              >
                <i className="bi bi-check-lg" aria-hidden="true" />
              </button>
            </div>
          ) : (
            <div className="ai-assistant__input-wrapper">
              <textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask your question..."
                className="ai-assistant__textarea"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              {speechSupported && !isAnswering && (
                <button
                  type="button"
                  className="ai-assistant__mic-btn"
                  onClick={startListening}
                  title="Voice input"
                  aria-label="Start voice input"
                >
                  <i className="bi bi-mic" aria-hidden="true" />
                </button>
              )}
              {isAnswering ? (
                <button
                  type="button"
                  className="ai-assistant__stop-btn"
                  onClick={stopAnswer}
                  title="Stop answering"
                  aria-label="Stop answering"
                >
                  <span className="ai-assistant__stop-icon" aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="ai-assistant__send-btn"
                  disabled={!query.trim()}
                  title="Send"
                  aria-label="Send message"
                >
                  <i className="bi bi-arrow-up" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
          <div className="ai-assistant__hint">
            <kbd>Enter</kbd> to send · <kbd>Shift</kbd> + <kbd>Enter</kbd> for a new line
          </div>
        </form>
      </footer>
    </div>
  );
}

export function AiAssistantDrawer({ open, onClose }) {
  useEffect(() => {
    if (!open) return undefined;

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="ai-assistant-drawer__backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="ai-assistant-drawer" role="dialog" aria-label="AI Assistant">
        <AiAssistant onClose={onClose} />
      </aside>
    </>
  );
}

export default AiAssistant;
